import {
  PopulatedThreadMessage,
  ThreadMessageGroup,
  ThreadMessageWithThreadData,
  ThreadType,
  WithId,
} from '@hb/shared'
import { useMemo, useRef } from 'react'
import { populateThreadMessage } from '../utils'

interface PopulatedCacheEntry {
  src: ThreadMessageWithThreadData
  prev: ThreadMessageWithThreadData | undefined
  next: ThreadMessageWithThreadData | undefined
  uid: string | null
  out: PopulatedThreadMessage
}

// Message object identity must survive unrelated data changes — every rendered
// row is memoized on its message prop, so rebuilding all messages on any group
// snapshot/page-in re-renders the whole list at once (visible jitter on mobile).
// Two cache layers keep references stable:
//  1. flattened+sorted messages per group, keyed by the group object's identity —
//     a subscription tick replaces only that group's object, so every other
//     group reuses its existing message objects
//  2. populated messages per message id, reused while the source message and its
//     prev/next neighbors are reference-equal — a page-in only creates new
//     objects for the new page and the one boundary message it touches
export const useMessagesFromGroups = (
  uid: string | null,
  threadType: ThreadType,
  threadId: string | null,
  allMessageGroups: Record<string, ThreadMessageGroup>,
  todayData: WithId<ThreadMessageGroup> | null,
) => {
  const groupMessagesCache = useRef(
    new WeakMap<ThreadMessageGroup, ThreadMessageWithThreadData[]>(),
  )
  const populatedCache = useRef(new Map<string, PopulatedCacheEntry>())
  // group objects are replaced when the thread changes, but reset defensively so
  // a stale entry can never leak messages stamped with the previous thread
  const cacheKey = useRef<string | null>(null)

  return useMemo<PopulatedThreadMessage[]>(() => {
    if (!threadId || !uid) return []

    const currentKey = `${threadType}:${threadId}`
    if (cacheKey.current !== currentKey) {
      groupMessagesCache.current = new WeakMap()
      populatedCache.current = new Map()
      cacheKey.current = currentKey
    }

    // the raw record values are the identity anchors — objectToArray would wrap
    // them in fresh objects on every call, defeating the cache
    const groupEntries = Object.entries(
      todayData ? { ...allMessageGroups, [todayData.id]: todayData } : allMessageGroups,
    ).sort(([, a], [, b]) => a.createdOn - b.createdOn)

    // groups are day-keyed and messages sorted within each group, so
    // concatenating in group order yields the same order as a global sort
    const flat: ThreadMessageWithThreadData[] = []
    for (const [groupId, group] of groupEntries) {
      let groupMessages = groupMessagesCache.current.get(group)
      if (!groupMessages) {
        groupMessages = Object.entries(group.messages).map(([messageId, message]) => ({
          ...message,
          threadId,
          messageGroupCreatedOn: group.createdOn,
          id: messageId,
          threadType,
          messageGroupId: groupId,
        }))
        groupMessages.sort((a, b) => a.createdOn - b.createdOn)
        groupMessagesCache.current.set(group, groupMessages)
      }
      for (const message of groupMessages) flat.push(message)
    }

    // rebuild the populated cache from scratch each pass so entries for trimmed
    // groups don't accumulate
    const prevCache = populatedCache.current
    const nextCache = new Map<string, PopulatedCacheEntry>()
    const result: PopulatedThreadMessage[] = new Array(flat.length)
    for (let i = 0; i < flat.length; i++) {
      const src = flat[i]
      const prev = i > 0 ? flat[i - 1] : undefined
      const next = i < flat.length - 1 ? flat[i + 1] : undefined
      const cached = prevCache.get(src.id)
      const out =
        cached &&
        cached.src === src &&
        cached.prev === prev &&
        cached.next === next &&
        cached.uid === uid
          ? cached.out
          : populateThreadMessage(uid, src, prev, next)
      nextCache.set(src.id, { src, prev, next, uid, out })
      result[i] = out
    }
    populatedCache.current = nextCache
    return result
  }, [threadId, threadType, allMessageGroups, todayData, uid])
}
